/** *************************************************
 * Anything related to brands should be based on
 * constants to be consistent.
 ************************************************** */

import { getBrand } from '@helpers/brand.utils';

export interface BrandConstant {
  code: Brand;
  /** Brand name display string */
  brandName: string;
  /** Brand name display string above footer legal text */
  brandNameFooter: string;
  brandNameLegal: string;
  /** Terms of Use redirect URL */
  termsOfUseLink: string;
  /** Customer support email */
  supportEmail: string;
  /** Customer support phone */
  supportPhone: string;
  /** Email address verification emails are sent from */
  verificationEmail: string;
  /** Root URL for the parent site */
  rootURL: string;
  /** Indicates if sqft values drop down should be displayed */
  showSqftValues: boolean;
  /** Indicates if My Quotes link should be displayed */
  shouldDisplayMyQuotes: boolean;
  /** Indicates if Plans & Prices MFE should be displayed */
  plansPricesDisplayMFE: boolean;
  /** Indicates if Plans & Prices page should be displayed */
  displayPlansPricesPage: boolean;
  /** Indicates if Plans & Prices page footer link should be shown */
  showPlansPricesPageFooterLink: boolean;
  /** Indicates if Home Matters Blog link should be shown in footer */
  showHomeMattersBlog: boolean;
  /** Indicates if Gift Frontdoor Unlimited button should be displayed */
  showGiftFrontdoorUnlimited: boolean;
  /** Footer link URLs */
  footerLinks: {
    realEstate: string;
  };
}

interface BrandConstants {
  AHS: BrandConstant;
  HSA: BrandConstant;
  HBW210: BrandConstant;
}

export enum Brand {
  AHS = 'AHS',
  HSA = 'HSA',
  HBW210 = 'HBW210',
}

const BrandConstantsValues: BrandConstants = {
  AHS: {
    code: Brand.AHS,
    brandName: 'American Home Shield',
    brandNameFooter: 'American Home Shield',
    brandNameLegal: 'American Home Shield',
    supportEmail: 'ahspartnersupport@ahs.com',
    supportPhone: '800-735-4663',
    termsOfUseLink: 'https://www.frontdoor.com/contractor-terms-of-use',
    verificationEmail: 'no-reply.pro.ahs.com@ahs.com',
    rootURL: 'https://www.ahs.com',
    showSqftValues: true,
    shouldDisplayMyQuotes: true,
    plansPricesDisplayMFE: false,
    displayPlansPricesPage: true,
    showPlansPricesPageFooterLink: true,
    showHomeMattersBlog: true,
    showGiftFrontdoorUnlimited: true,
    footerLinks: {
      realEstate: 'https://www.ahs.com/real-estate/',
    },
  },
  HSA: {
    code: Brand.HSA,
    brandName: 'HSA',
    brandNameFooter: 'Home Security of America',
    brandNameLegal: 'Home Security of America',
    supportEmail: 'hsapartnersupport@onlinehsa.net',
    supportPhone: '800-367-1448',
    termsOfUseLink: 'https://www.frontdoor.com/contractor-terms-of-use',
    verificationEmail: 'no-reply.own.onlinehsa@onlinehsa.com',
    rootURL: 'https://www.onlinehsa.com',
    showSqftValues: false,
    shouldDisplayMyQuotes: false,
    plansPricesDisplayMFE: false,
    displayPlansPricesPage: false,
    showPlansPricesPageFooterLink: false,
    showHomeMattersBlog: true,
    showGiftFrontdoorUnlimited: true,
    footerLinks: {
      realEstate: 'https://www.ahs.com/real-estate/',
    },
  },
  HBW210: {
    code: Brand.HBW210,
    brandName: '2-10',
    brandNameFooter: '',
    brandNameLegal: 'Home Buyers Warranty Corporation',
    supportEmail: '2-10partnersupport@2-10.com',
    supportPhone: '720-531-6723',
    termsOfUseLink: 'https://www.frontdoor.com/contractor-terms-of-use',
    verificationEmail: 'no-reply.pro.2-10@2-10.com',
    rootURL: 'https://www.2-10.com',
    showSqftValues: true,
    shouldDisplayMyQuotes: true,
    plansPricesDisplayMFE: true,
    displayPlansPricesPage: true,
    showPlansPricesPageFooterLink: true,
    showHomeMattersBlog: false,
    showGiftFrontdoorUnlimited: false,
    footerLinks: {
      realEstate: 'https://www.2-10.com/brokers-agents/',
    },
  }
};

/** Get constants for a brand. Defaults to brand.utils:getBrand() if unprovided. */
export default function getBrandConstants(brand: string = getBrand()): BrandConstant {
  if (BrandConstantsValues[brand]) {
    return BrandConstantsValues[brand];
  }
  console.warn('Unknown brand, defaulting to AHS');
  return BrandConstantsValues.AHS;
}
